import { Button, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import { Close as CloseIcon, MailOutlineOutlined, Download, Mail } from "@mui/icons-material";

import React, { useState, useCallback, useMemo, useEffect } from "react";
import InputField from "components/Input/InputField";
import useKothar from "context/useKothar";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { differenceInDays, differenceInCalendarDays, format } from "date-fns";
import { formatDisplayDate } from "utils/dateFormat";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { BiLogoGmail } from "react-icons/bi";
import { useNavigate } from "react-router-dom";

const VisaExpiringStudentsModal = ({ open, handleClose }) => {
  const [
    { paginatedClientList, isPaginatedClientLoading, pagination },
    { refetchPaginatedClient, setPagination },
  ] = useKothar();
  const [number, setNumber] = useState(30);
  const [untilDate, setUntilDate] = useState("");
  const [debouncedNumber, setDebouncedNumber] = useState(30);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedNumber(number), 350);
    return () => clearTimeout(timer);
  }, [number]);

  const days = untilDate
    ? Math.max(0, differenceInCalendarDays(new Date(untilDate), new Date()))
    : debouncedNumber;

  const visaExpiringFrom = format(new Date(), "yyyy-MM-dd");
  const visaExpiringTo = untilDate || "";
  const visaExpiringDays = untilDate ? "" : days;
  const navigate = useNavigate();
  const fetchVisaExpiring = useCallback(() => {
    refetchPaginatedClient({
      pageIndex: 0,
      visaExpiringDays,
      visaExpiringFrom: untilDate ? visaExpiringFrom : "",
      visaExpiringTo,
    });
  }, [refetchPaginatedClient, untilDate, visaExpiringDays, visaExpiringFrom, visaExpiringTo]);

  useEffect(() => {
    if (!open) return;
    if (untilDate || days > 0) fetchVisaExpiring();
  }, [open, days, untilDate]);

  const filteredList = paginatedClientList?.content || [];
  const isFetching = isPaginatedClientLoading;

  const handleSubmit = useCallback(() => {
    fetchVisaExpiring();
  }, []);

  const handleDialogClose = useCallback(() => {
    refetchPaginatedClient({
      pageIndex: 0,
      visaExpiringDays: "",
      visaExpiringFrom: "",
      visaExpiringTo: "",
    });
    handleClose();
  }, [handleClose, refetchPaginatedClient]);

  const handlePaginationChange = useCallback(
    (updater) => {
      setPagination((prev) => {
        const current = {
          pageIndex: prev.pageIndex ?? 0,
          pageSize: prev.pageSize ?? 20,
        };
        const next = typeof updater === "function" ? updater(current) : updater;
        return {
          ...prev,
          pageIndex: next?.pageIndex ?? current.pageIndex,
          pageSize: next?.pageSize ?? current.pageSize,
        };
      });
    },
    [setPagination]
  );

  const columns = useMemo(
    () => [
      {
        header: "Client Name",
        accessorKey: "name",
      },
      {
        header: "Email",
        accessorKey: "email",
      },
      {
        header: "Number",
        accessorKey: "number",
      },
      {
        header: "Visa Expiry",
        accessorKey: "visaExpiry",
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {row.original.visaExpiry ? (
              <div className="flex items-center gap-2">
                <span>{row.original.visaExpiry}</span>
              </div>
            ) : (
              <span>N/A</span>
            )}
          </div>
        ),
      },
      {
        header: "Days Until Expiry",
        accessorKey: "daysUntilExpiry",
        size: 50,
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {differenceInDays(new Date(row.original.visaExpiry), new Date())}
          </div>
        ),
      },

      {
        header: "Action",
        accessorKey: "action",
        Cell: ({ row }) => (
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="contained"
              onClick={() =>
                //   navigate(`/admin/client/edit/${row.original?.id}`, {
                //     state: {
                //       item: row.original,
                //     },
                //     target: "_blank",
                //   })
                window.open(`/admin/client/edit/${row.original?.id}`, "_blank")
              }
            >
              Edit
            </Button>
            <Tooltip title={"Send Email to the Client by Gmail Email Service"} arrow>
              <BiLogoGmail
                className="text-primary text-2xl"
                onClick={() => {
                  const mailtoLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${row?.original?.email}`;
                  window.open(mailtoLink, "_blank");
                }}
              />
            </Tooltip>
            <Tooltip title={"Send Email to the Client by Kothar Email Service"} arrow>
              <Mail
                className="text-primary text-2xl"
                onClick={() => {
                  navigate("/admin/emails/compose", {
                    state: {
                      email: row?.original?.email,
                    },
                  });
                }}
              />
            </Tooltip>
          </div>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredList,
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: "calc(70vh - 300px)",
        minHeight: "calc(70vh - 300px)",
        overflowY: "auto",
        position: "relative",
      },
    },
    initialState: {
      pagination: {
        pageSize: paginatedClientList?.pageSize || 20,
        pageIndex: paginatedClientList?.pageIndex || 0,
      },
    },
    muiPaginationProps: { rowsPerPageOptions: [20, 50, 100] },
    pageCount: paginatedClientList?.totalPages || 0,
    rowCount: paginatedClientList?.totalItems || 0,
    onPaginationChange: handlePaginationChange,
    enablePagination: true,
    enableRowNumbers: true,
    manualPagination: true,
    autoResetPageIndex: false,
    paginationDisplayMode: "pages",
    state: {
      pagination: {
        pageIndex: pagination?.pageIndex ?? 0,
        pageSize: pagination?.pageSize ?? 20,
      },
      isLoading: isFetching,
      showProgressBars: isFetching,
    },
  });

  const handleReset = useCallback(() => {
    setNumber(30);
    setDebouncedNumber(30);
    setUntilDate("");
  }, []);

  const handleExportToExcel = useCallback(() => {
    if (filteredList.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      // Prepare data for Excel export
      const excelData = filteredList.map((client, index) => {
        const visaExpiryDate = client.visaExpiry ? new Date(client.visaExpiry) : null;
        const daysUntilExpiry = visaExpiryDate
          ? differenceInDays(visaExpiryDate, new Date())
          : "N/A";

        return {
          "S.N.": index + 1,
          "Client Name": client.name || "",
          Email: client.email || "",
          "Phone Number": client.number || "",
          "Visa Expiry Date": client.visaExpiry || "N/A",
          "Days Until Expiry": daysUntilExpiry,
          "Passport Number": client.passportNumber || "",
          "Visa Status": client.visaStatus || "",
        };
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Visa Expiring Clients");

      // Set column widths
      const columnWidths = [
        { wch: 5 }, // S.N.
        { wch: 25 }, // Client Name
        { wch: 30 }, // Email
        { wch: 15 }, // Phone Number
        { wch: 18 }, // Visa Expiry Date
        { wch: 18 }, // Days Until Expiry
        { wch: 18 }, // Passport Number
        { wch: 15 }, // Visa Status
      ];
      worksheet["!cols"] = columnWidths;

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      // Create blob and download
      const data = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Generate filename with date range
      const today = format(new Date(), "yyyy-MM-dd");
      let endDateStr = today;
      if (untilDate) {
        endDateStr = format(new Date(untilDate), "yyyy-MM-dd");
      } else if (number > 0) {
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + number);
        endDateStr = format(endDate, "yyyy-MM-dd");
      }
      const filename = `Visa_Expiring_Clients_${today}_to_${endDateStr}.xlsx`;

      saveAs(data, filename);
      toast.success("Data exported to Excel successfully!");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export data to Excel");
    }
  }, [filteredList, number, untilDate]);

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          className: "rounded-lg",
        }}
      >
        <DialogTitle className="flex justify-between items-center border-b pb-3">
          <div>
            <h2 className="text-xl font-semibold text-app-text">Visa Expiring Clients </h2>
          </div>
          <IconButton onClick={handleDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="pb-6 pt-2 min-h-[500px]">
          <div className="flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col items-center gap-2">
              <label className="text-lg text-app-text-secondary block font-semibold">
                Filter Visa Expiring Clients
              </label>
              <p className="text-sm text-app-text-secondary">
                Enter number of days or select an end date
              </p>
            </div>
            <div className="flex flex-wrap justify-center items-end gap-4 w-full max-w-4xl">
              <div className="flex flex-col gap-1">
                <label className="text-sm text-app-text-secondary font-medium">
                  Number of Days
                </label>
                <InputField
                  placeholder="Number of Days"
                  value={number}
                  onChange={(e) => setNumber(Number(e.target.value) || 0)}
                  outerClassName="w-[200px] border-app-border"
                  type="number"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm text-app-text-secondary font-medium">
                  Or Until Date (Optional)
                </label>
                <InputField
                  placeholder="Select End Date"
                  value={untilDate}
                  onChange={(e) => setUntilDate(e.target.value)}
                  outerClassName="w-[200px] border-app-border"
                  type="date"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="contained" onClick={handleSubmit} className="min-w-[100px]">
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} className="min-w-[100px]">
                  Reset
                </Button>
                {paginatedClientList.totalItems > 0 && (
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#2e7d32",
                      "&:hover": { backgroundColor: "#1b5e20" },
                    }}
                    startIcon={<Download />}
                    onClick={handleExportToExcel}
                    className="min-w-[140px]"
                  >
                    Export Excel
                  </Button>
                )}
              </div>
            </div>
          </div>
          {console.log(filteredList)}

          <div className="mt-4">
            {paginatedClientList.totalItems > 0 && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-blue-800 font-medium">
                    Found {paginatedClientList.totalItems} clients with visa expiring
                    {untilDate
                      ? ` until ${formatDisplayDate(untilDate)}`
                      : ` within ${number} days`}
                  </p>
                </div>
              </div>
            )}
            {paginatedClientList.totalItems === 0 && (number > 0 || untilDate) && (
              <div className="mb-4 p-3 bg-app-surface-muted border border-app-border rounded-lg">
                <p className="text-app-text-secondary">
                  No clients found with visa expiring
                  {untilDate
                    ? ` until ${formatDisplayDate(untilDate)}`
                    : number > 0
                    ? ` within ${number} days`
                    : ""}
                </p>
              </div>
            )}
            <MaterialReactTable table={table} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VisaExpiringStudentsModal;
